import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useLanguage } from '../LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, RefreshCw, Upload, X, FlipHorizontal } from 'lucide-react';
import { Button } from '../ui/button';

export default function CameraCapture({ onCapture, capturedImage, onClear }) {
  const { t, language } = useLanguage();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [error, setError] = useState(null);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          setCameraReady(true);
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError(language === 'he' 
        ? 'לא ניתן לגשת למצלמה. אנא אשר גישה למצלמה או העלה תמונה.'
        : 'Cannot access camera. Please allow camera access or upload an image.');
    }
  }, [facingMode, stream, language]);

  useEffect(() => {
    if (!capturedImage) {
      startCamera();
    }
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [capturedImage]);

  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    startCamera();
  };

  const startCountdown = () => {
    setCountdown(3);
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev === 1) {
          clearInterval(timer);
          capturePhoto();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    
    // Flip horizontally if using front camera
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    
    ctx.drawImage(video, 0, 0);
    
    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], 'captured-photo.jpg', { type: 'image/jpeg' });
        const previewUrl = URL.createObjectURL(blob);
        onCapture({ previewUrl, file });

        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      }
    }, 'image/jpeg', 0.9);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    onCapture({ previewUrl, file });

    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  if (capturedImage) {
    return (
      <div className="relative rounded-2xl overflow-hidden bg-slate-900">
        <img
          src={capturedImage}
          alt="Captured"
          className="w-full aspect-[3/4] object-cover"
        />
        <Button
          onClick={onClear}
          variant="destructive"
          size="icon"
          className="absolute top-4 right-4 rounded-full"
        >
          <X className="w-5 h-5" />
        </Button>
        <div className="absolute bottom-4 left-4 right-4">
          <Button 
            onClick={onClear}
            className="w-full bg-slate-700 hover:bg-slate-600 text-white font-medium"
          >
            <RefreshCw className="w-4 h-4 me-2" />
            {t('retake')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative rounded-2xl overflow-hidden bg-slate-900 aspect-[3/4]">
        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
            <Camera className="w-16 h-16 text-slate-600 mb-4" />
            <p className="text-slate-400 mb-6">{error}</p>
            <Button onClick={startCamera} className="bg-slate-700 hover:bg-slate-600 text-white font-medium">
              <RefreshCw className="w-4 h-4 me-2" />
              {language === 'he' ? 'נסה שוב' : 'Try Again'}
            </Button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
            />
            
            {/* Camera Controls */}
            <div className="absolute top-4 right-4">
              <Button
                onClick={switchCamera}
                variant="ghost"
                size="icon"
                className="bg-black/30 text-white hover:bg-black/50 rounded-full"
              >
                <FlipHorizontal className="w-5 h-5" />
              </Button>
            </div>

            {/* Countdown Overlay */}
            <AnimatePresence>
              {countdown !== null && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  className="absolute inset-0 flex items-center justify-center bg-black/50"
                >
                  <motion.span
                    key={countdown}
                    initial={{ opacity: 0, scale: 2 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    className="text-9xl font-bold text-white"
                  >
                    {countdown}
                  </motion.span>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
        
        <canvas ref={canvasRef} className="hidden" />
      </div>

      <div className="flex gap-3">
        <Button
          onClick={startCountdown}
          disabled={!cameraReady || countdown !== null}
          className="flex-1 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white h-14 text-lg rounded-xl"
        >
          <Camera className="w-5 h-5 me-2" />
          {t('takePhoto')}
        </Button>
        
        <Button
          onClick={() => fileInputRef.current?.click()}
          className="h-14 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium"
        >
          <Upload className="w-5 h-5" />
        </Button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>
    </div>
  );
}
